import { motion } from "framer-motion";
import { useEffect } from "react";
import heroVideo from "../assests/dubbing.mp4"; // 🎬 Your hero background video

export default function DubbingVoiceover() {
   
  return (
    <div className="bg-gray-50 text-gray-900 font-raleway">
      {/* Hero Section with Video Background */}
      <section className="relative w-full h-[70vh] text-white flex items-center justify-center text-center">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={heroVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70"></div>

        <div className="relative z-10 px-6 max-w-3xl">
          <motion.h1
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-bold mb-6 tracking-tight drop-shadow-lg font-orbitron"
          >
            Dubbing & Voiceover Services
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed text-gray-200"
          >
            Give your content the voice it deserves — authentic, engaging, and multilingual dubbing & voiceover solutions.
          </motion.p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-6 text-center max-w-4xl mx-auto">
        <h2 className="text-4xl font-semibold mb-6 text-gray-900 font-orbitron">
          Why Dubbing & Voiceovers Matter
        </h2>
        <p className="text-lg text-gray-700 leading-relaxed">
          Voice is the soul of content. Whether it’s a movie, e-learning course, or AI dataset,
          the right voice creates emotional impact and cultural connection. 
          Our dubbing and voiceover services ensure your message reaches people in their own
          language, tone, and context.
        </p>
      </section>

      {/* Services Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-gray-100 via-white to-gray-100">
        <h2 className="text-4xl font-semibold text-center mb-12 text-gray-900 font-orbitron">
          Our Services
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {[
            {
              title: "🎬 Film & TV Dubbing",
              desc: "Native-language voice replacement with lip-sync precision.",
            },
            {
              title: "📚 E-Learning Voiceovers",
              desc: "Clear and engaging narration for courses and training modules.",
            },
            {
              title: "📢 Commercials & Ads",
              desc: "Impactful brand storytelling across multiple languages.",
            },
            {
              title: "🤖 AI Voice Datasets",
              desc: "Natural, multilingual voice recordings for AI & speech models.",
            },
            {
              title: "🎙 Corporate & IVR",
              desc: "Professional voiceovers for presentations, tutorials, and call systems.",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              whileHover={{ scale: 1.05 }}
              className="bg-white p-6 rounded-2xl shadow-md hover:shadow-xl transition"
            >
              <h3 className="text-xl font-semibold mb-3 text-indigo-700">{item.title}</h3>
              <p className="text-gray-700 leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-6">
        <h2 className="text-4xl font-semibold text-center mb-12 text-gray-900 font-orbitron">
          How We Work
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 max-w-6xl mx-auto text-center">
          {[
            "Script analysis & adaptation",
            "Voice casting & selection",
            "Studio-quality recording",
            "Synchronization & editing",
            "Final QA & delivery",
          ].map((step, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: idx * 0.2 }}
              viewport={{ once: true }}
              className="p-6 bg-white rounded-xl shadow-md hover:shadow-lg"
            >
              <div className="text-4xl mb-3">{`${idx + 1}️⃣`}</div>
              <p className="text-gray-700 leading-relaxed">{step}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Applications Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-indigo-50 via-white to-purple-50">
        <h2 className="text-4xl font-semibold text-center mb-12 text-gray-900 font-orbitron">
          Applications
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {[
            { title: "🎥 Entertainment", desc: "Movies, series, and documentaries with local voices." },
            { title: "🎧 Streaming & Podcasts", desc: "Localized and original voiceovers for media." },
            { title: "🏫 Education", desc: "E-learning modules and audiobooks." },
            { title: "🏢 Enterprises", desc: "Training videos and corporate communications." },
            { title: "🤖 AI & Tech", desc: "Conversational AI and speech recognition datasets." },
            { title: "🛒 Marketing", desc: "Ads, social media, and promotional campaigns." },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              whileHover={{ scale: 1.05 }}
              className="bg-white p-6 rounded-2xl shadow hover:shadow-xl transition"
            >
              <h3 className="font-semibold mb-2 text-lg text-indigo-700">
                {item.title}
              </h3>
              <p className="text-gray-700 leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-6 text-center max-w-4xl mx-auto">
        <h2 className="text-4xl font-semibold mb-6 text-gray-900 font-orbitron">
          Why Choose Us
        </h2>
        <p className="text-lg text-gray-700 leading-relaxed">
          ✅ 80+ languages & regional dialects <br />
          ✅ Native voice artists with cultural expertise <br />
          ✅ Studio-quality sound and post-production <br />
          ✅ Lip-sync dubbing & culturally adapted scripts <br />
          ✅ Scalable solutions for enterprise-level projects
        </p>
      </section>

      {/* Call to Action */}
      <section className="py-24 text-center bg-gradient-to-r from-indigo-600 to-purple-700 text-white">
        <h2 className="text-4xl md:text-5xl font-bold mb-6 font-orbitron">
          Give Your Brand a Voice That Resonates Globally
        </h2>
        <p className="text-lg max-w-xl mx-auto mb-6">
          From entertainment to enterprise, our dubbing & voiceover solutions 
          connect with audiences worldwide.
        </p>
        <motion.button
          whileHover={{ scale: 1.1 }}
          className="bg-white text-indigo-700 font-semibold px-6 py-3 rounded-xl shadow-md hover:bg-gray-100 transition"
        >
          🎙 Get Started Today
        </motion.button>
      </section>
    </div>
  );
}
